import { motion } from "framer-motion";
import { ArrowUpRight, Instagram, MessageCircle } from "lucide-react";

const FEED_SHOT =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_story-in-motion-7/artifacts/ilis4p2c_IMG_8977.jpeg";

const Corners = () => (
  <>
    <span className="pointer-events-none absolute left-2 top-2 z-10 h-3 w-3 border-l border-t border-white/40" />
    <span className="pointer-events-none absolute right-2 top-2 z-10 h-3 w-3 border-r border-t border-white/40" />
    <span className="pointer-events-none absolute bottom-2 left-2 z-10 h-3 w-3 border-b border-l border-white/40" />
    <span className="pointer-events-none absolute bottom-2 right-2 z-10 h-3 w-3 border-b border-r border-white/40" />
  </>
);

export const Work = () => (
  <section
    id="work"
    data-testid="work-section"
    className="bg-[#0D0D0F] px-5 py-24 sm:px-10 sm:py-36"
  >
    <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 lg:grid-cols-[1.3fr_1fr] lg:gap-20">
      <div>
        <motion.span
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          className="mb-8 block font-mono text-[10px] uppercase tracking-[0.35em] text-[#8E8E93] sm:text-xs"
        >
          03 — THE WORK
        </motion.span>

        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="font-display text-[clamp(2.6rem,7vw,6.5rem)] font-black uppercase leading-[0.9] tracking-tight text-[#F3F3F5]"
        >
          MY EDITS LIVE ON <span className="text-[#E63946]">INSTAGRAM.</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mt-8 max-w-xl text-sm leading-relaxed text-[#8E8E93] sm:text-base"
        >
          Every new edit, vlog, and experiment posts to{" "}
          <span className="text-[#F3F3F5]">@j.erem1ah</span> first — that's the living archive.
          Scroll the feed and you'll see exactly how I cut real life into stories.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="mt-6 max-w-xl text-sm leading-relaxed text-[#8E8E93] sm:text-base"
        >
          Want proof for your own footage? Start a project and{" "}
          <span className="text-[#F3F3F5]">
            I'll send demos straight to your messages
          </span>{" "}
          — no commitment, just a taste of what your clips could become.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <a
            data-testid="work-instagram-button"
            href="https://www.instagram.com/j.erem1ah/"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 bg-[#F3F3F5] px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-[#050505] transition-colors duration-300 hover:bg-[#E63946] hover:text-white"
          >
            <Instagram className="h-4 w-4" strokeWidth={1.5} />
            SEE THE EDITS
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5} />
          </a>
          <a
            data-testid="work-message-button"
            href="https://ig.me/m/j.erem1ah"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 border border-white/25 px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-[#F3F3F5] transition-colors duration-300 hover:border-white hover:bg-white hover:text-[#050505]"
          >
            <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
            MESSAGE ME
          </a>
        </motion.div>

        <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.3em] text-[#3a3a3f]">
          DEMOS SENT IN THE DMS — JUST ASK.
        </p>
      </div>

      <motion.a
        data-testid="work-feed-frame"
        href="https://www.instagram.com/j.erem1ah/"
        target="_blank"
        rel="noopener noreferrer"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="group relative block"
      >
        <div className="relative overflow-hidden border border-white/10">
          <Corners />
          <img
            src={FEED_SHOT}
            alt="@j.erem1ah Instagram feed — latest edits by Jeremiah Womack"
            className="aspect-[9/16] w-full object-cover object-top transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
          />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/80 to-transparent" />
          <span className="absolute bottom-4 left-4 flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.3em] text-white/80">
            <span className="animate-rec inline-block h-1.5 w-1.5 rounded-full bg-[#E63946]" />
            LIVE FEED — @J.EREM1AH
          </span>
        </div>
        <span className="mt-3 block font-mono text-[10px] uppercase tracking-[0.3em] text-[#3a3a3f]">
          FIG. 02 — THE LIVING ARCHIVE, TAP TO OPEN
        </span>
      </motion.a>
    </div>
  </section>
);
